import { useState, useContext, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { PageContext } from "../contexts/PageContext";
import { createRemoteControl, RemoteControl } from "../models/RemoteControl";
import { deleteRecord, loadOrgModel, loadOrgRecords, saveModel } from "../api/connection";
import Box from "@mui/material/Box/Box";
import { Check, CheckCircleOutline, DeckOutlined, Delete, QrCode, QuestionMarkOutlined, Save, TransferWithinAStation, UploadFile } from "@mui/icons-material";
import { Backdrop, CircularProgress, CssBaseline, Grid2, MenuItem, TextField } from "@mui/material";
import PageHeader from "../components/PageHeader";
import Minibar from "../components/Minibar";
import { TokenField } from "../components/TokenField";
import NavigatableSelect from "../components/NavigatableSelect";
import { createRobot } from "../models/Robot";
import { createDeployment } from "../models/Deployment";
import { showInputDialog, showSnackbar } from "../components/UniversalDialog";
import { uploadDeploymentPack } from "../api/deployment-io";
import QRCode from "react-qr-code";
import { getApkVersions } from "./HMISupportPage";

export function EditRemotePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [robotOrDeploymentChanged, setRobotOrDeploymentChanged] = useState<boolean>(false);
  const { user } = useContext(PageContext);
  const [remote, setRemote] = useState<RemoteControl | null>(null);
  const [qrCode, setQrCode] = useState('');
  const [robotOptions, setRobotOptions] = useState<{ label: string; value: string }[]>([]);
  const [deploymentOptions, setDeploymentOptions] = useState<{ label: string; value: string }[]>([]);
  const [apkVersions, setApkVersions] = useState<string[]>([]);
  useEffect(() => {
    // Fetch remote control data by ID
    const fetchRemote = async () => {
      const rs = await loadOrgRecords('robots', createRobot);
      const robotOpts = rs.map(r => ({ label: r.name || r._id, value: r._id }));
      setRobotOptions(robotOpts);
      const ds = await loadOrgRecords('deployments', createDeployment);
      const deploymentOpts = ds.map(d => ({ label: d.name || d._id, value: d._id }));
      setDeploymentOptions(deploymentOpts);
      if (id === 'new') {
        setRemote({ ...createRemoteControl(), orgId: user?.orgId || "" });
      } else {
        const rc = await loadOrgModel(createRemoteControl(id));
        // console.log({ rc });
        setRemote(rc);
      }
      const apkVers = await getApkVersions();
      setApkVersions(['', ...apkVers.map(v => v.version)]);
      setLoading(false);
    };

    if (id) {
      fetchRemote();
    } else {
      setError("No remote ID provided.");
      setLoading(false);
    }
  }, [id]);
  const saveRemote = async () => {
    if (!remote!.blobUrl) {
      remote!.blobUrl = process.env.REACT_APP_BLOB_URL || '';
    }
    await saveModel(remote!);
    setRemote({ ...remote! });
    if (robotOrDeploymentChanged) {
      showSnackbar("Robot or Deployment changed. Uploading deployment pack...");
      await uploadDeploymentPack(remote!.deploymentId, remote!._id);
      showSnackbar("Deployment pack uploaded.");
      setRobotOrDeploymentChanged(false);
      const updatedRemote = await loadOrgModel(createRemoteControl(remote!._id));
      setRemote(updatedRemote);
    }
    if (id === 'new') {
      navigate(`/edit-remote/${remote!._id}`);
    }
  }
  const deleteRemote = async () => {
    await deleteRecord("remotecontrols", remote!);
    navigate("/remotes");
  }
  if (!remote) {
    return (
      <Backdrop
        open={true}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }
  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          m: 0,
          backgroundColor: "#212121",
          color: "#ECECEC",
          minHeight: "100vh",
        }}
      >
        <PageHeader
          title={`Edit Remote: ${remote.name || "New Remote"}`} // Update header
          rightComponent={[
            <Minibar
              minibarItems={
                id === "new"
                  ? [
                    {
                      text: "Save New Remote",
                      icon: <Save />,
                      onClick: saveRemote,
                    },
                  ]
                  : [

                    {
                      text: "Update Remote",
                      icon: <Save />,
                      onClick: saveRemote,
                    },
                    {
                      text: "Toggle QR Code",
                      icon: <QrCode />,
                      onClick: () => {
                        if (qrCode) {
                          setQrCode('');
                          return;
                        }
                        const qrData = `${remote._id}|${remote.token}|${process.env.REACT_APP_SIGNALING_URL}|${remote.blobUrl}`;
                        setQrCode(qrData);
                      },
                    },
                    {
                      text: 'Upload device package',
                      icon: <UploadFile />,
                      onClick: async () => {
                        if (remote) {
                          showSnackbar("Uploading deployment pack...");
                          await uploadDeploymentPack(remote.deploymentId, remote._id);
                          showSnackbar("Deployment pack uploaded.");
                          const updatedRemote = await loadOrgModel(createRemoteControl(remote._id));
                          setRemote(updatedRemote);
                        }
                      },
                    },
                    {
                      text: "Delete Remote",
                      icon: <Delete />,
                      onClick: deleteRemote,
                    },
                  ]
              }
              barLength={4}
            />,
          ]}
        />
        <Grid2 container direction={'row'}>
          <Grid2 container spacing={2} sx={{
            mt: 3, maxWidth: 600, ml: 3
          }}>
            <TextField label="ID" fullWidth value={remote._id} />
            <TextField
              label="Name"
              fullWidth
              value={remote.name}
              onChange={(e) => setRemote({ ...remote, name: e.target.value })}
            />
            <TextField
              label="Serial Number"
              fullWidth
              value={remote.serialNumber}
              onChange={(e) => setRemote({ ...remote, serialNumber: e.target.value })}
            />
            <Grid2 container width={'100%'} spacing={0} direction={'row'}>
              <Grid2 width={'50%'}>
                <TextField
                  label="Current app version"
                  fullWidth
                  disabled
                  value={remote.version}
                  onChange={(e) => { }}
                />
              </Grid2>
              <Grid2 width={'50%'}>
                <TextField
                  label="Required app version"
                  fullWidth
                  select
                  value={remote.requiredAppVersion}
                  onChange={(e) => setRemote({ ...remote, requiredAppVersion: e.target.value })}
                >
                  {apkVersions.map((ver) => (
                    <MenuItem key={ver} value={ver}>{ver ? ver : 'No restrictions'}</MenuItem>
                  ))}
                </TextField>
              </Grid2>
            </Grid2>
            <TextField
              label="Deployment pack version"
              fullWidth
              value={remote.deploymentPackStamp ? new Date(remote.deploymentPackStamp).toLocaleString() : 'Never'}
              disabled
              slotProps={{
                input: {
                  endAdornment: remote.deploymentPackStamp && remote.deploymentPackStamp === remote.deploymentPackStampOnDevice ? <CheckCircleOutline sx={{ mr: 1 }} /> : <QuestionMarkOutlined sx={{ mr: 1 }} />
                }
              }}
            />
            <NavigatableSelect
              label="Robot"
              value={remote.robotId}
              fullWidth
              onChange={(val) => {
                setRemote({ ...remote, robotId: val.target.value });
                setRobotOrDeploymentChanged(true);
              }}
              options={robotOptions}
              linkTo={`/edit-robot/${remote.robotId}`}
            />
            <NavigatableSelect
              label="Deployment"
              value={remote.deploymentId}
              fullWidth
              onChange={(val) => {
                setRemote({ ...remote, deploymentId: val.target.value });
                setRobotOrDeploymentChanged(true);
              }}
              options={deploymentOptions}
              linkTo={`/edit-deployment/${remote.deploymentId}`}
            />

            {!remote._id &&
              <TokenField
                value={remote.token}
                onChange={(val) => setRemote({ ...remote, token: val })}
              />
            }
            {remote._id &&
              <TextField label="Token" fullWidth value={remote.token} disabled />
            }
            <TextField label="Blob URL" fullWidth value={remote.blobUrl} onChange={(e) => setRemote({ ...remote, blobUrl: e.target.value })} />

            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={4}
              value={remote.description}
              onChange={(e) => setRemote({ ...remote, description: e.target.value })}
            />

          </Grid2>
          {qrCode && (
            <Box sx={{ marginLeft: 20, marginTop: 5, textAlign: 'center' }}>
              <QRCode
                value={qrCode}
                size={512}
                bgColor={'black'}
                fgColor={'white'}
                level={'H'}
              />
              <Box>Scan this QR code to configure the remote control.</Box>
            </Box>
          )}
        </Grid2>
      </Box>
    </Box>
  );
}